import React, { useMemo, useState } from 'react'

import { HexColorPicker, HexColorInput } from 'react-colorful'
import { debounce } from 'lodash'

import { ClickAwayListener } from '@mui/base/ClickAwayListener'
import { Box, Popper, Stack, useTheme } from '@mui/material'

// Plain (value / onChange) color picker mirroring ColorPickerField (the Studio
// theme picker) — same swatch + hex input + react-colorful gradient popover — but
// without the react-hook-form Controller, so it drops into plain useState forms.

interface ColorPickerProps {
  value: string
  onChange: (hex: string) => void
}

export const ColorPicker = ({ value, onChange }: ColorPickerProps) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [tempColor, setTempColor] = useState<string | null>(null)

  const debouncedOnChange = useMemo(
    () =>
      debounce((color: string) => {
        onChange(color)
        setTempColor(null)
      }, 100),
    [onChange],
  )

  const handlePickerChange = (color: string) => {
    setTempColor(color)
    debouncedOnChange(color)
  }

  const displayColor = tempColor || value

  return (
    <Stack direction="row" spacing={2} alignItems="center" width="100%" position="relative">
      {/* Swatch preview — click to open the gradient popover. */}
      <Box
        onClick={e => setAnchorEl(e.currentTarget.parentNode as HTMLElement)}
        sx={{
          position: 'absolute',
          left: 10,
          top: 10,
          zIndex: 1,
          width: 24,
          height: 24,
          borderRadius: 1,
          cursor: 'pointer',
          border: `0.5px solid ${theme.palette.grey[300]}`,
          backgroundColor: displayColor,
          transition: 'border-color 100ms linear',
          '&:hover': { borderColor: theme.palette.primary.main },
        }}
      />

      <Box sx={{ flex: 1, position: 'relative', ml: '0 !important' }}>
        <HexColorInput
          color={displayColor}
          onChange={onChange}
          prefixed
          style={{
            width: '100%',
            padding: '10px 12px 10px 44px',
            fontSize: '16px',
            height: '44px',
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: '8px',
            backgroundColor: theme.palette.grey[50],
            outline: 'none',
            fontFamily: 'inherit',
            color: theme.palette.text.primary,
            cursor: 'text',
            transition: 'border-color 150ms linear',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = theme.palette.brand[600]
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = theme.palette.grey[200]
          }}
        />

        {anchorEl && (
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Popper
              placement="bottom-start"
              open={!!anchorEl}
              anchorEl={anchorEl}
              disablePortal={false}
              modifiers={[
                { name: 'flip', enabled: true, options: { fallbackPlacements: ['top-start'] } },
                { name: 'preventOverflow', enabled: true, options: { padding: 8 } },
              ]}
              sx={{
                backgroundColor: theme.palette.white[100],
                borderRadius: 2,
                zIndex: 1500,
                padding: 2,
                boxShadow: theme.shadows[3],
                // The house trigger→popover gap, in absolute px. Was `mt: 1` = 4px.
                mt: '8px',
                '& .react-colorful__saturation-pointer, .react-colorful__hue-pointer': {
                  width: '16px',
                  height: '16px',
                },
              }}
            >
              <Box onClick={e => e.stopPropagation()}>
                <HexColorPicker color={displayColor} onChange={handlePickerChange} />
              </Box>
            </Popper>
          </ClickAwayListener>
        )}
      </Box>
    </Stack>
  )
}
